/**
 * Hero Carousel Block
 *
 * Only the modules the hero actually uses are imported, not swiper/bundle —
 * webpack splits everything under node_modules/swiper into
 * dist/assets/js/shared/swiper.js, and generate-acf-blocks.js makes that chunk
 * a dependency of this script (see the `blockUsesSwiper` check there), so
 * several carousel blocks on one page still share a single copy.
 *
 * The matching CSS is hand-written in hero-carousel.scss rather than imported
 * from swiper/css: the block only needs a dozen rules, and this hero is above
 * the fold.
 */

import Swiper from 'swiper';
import { A11y, Autoplay, EffectFade, Pagination } from 'swiper/modules';

const prefersReducedMotion = () =>
    window.matchMedia('(prefers-reduced-motion: reduce)').matches;

/**
 * Keep the faded-out slides out of the tab order and away from screen readers.
 *
 * Swiper's fade effect leaves every slide in the DOM at opacity 0, so without
 * this a visitor tabbing through the page would land on the CTA of a slide
 * they cannot see.
 */
function syncInertSlides(swiper) {
    swiper.slides.forEach((slide) => {
        slide.inert = !slide.classList.contains('swiper-slide-active');
    });
}

function initHeroCarousel(scope = document) {
    scope.querySelectorAll('.hero-carousel__carousel').forEach((carousel) => {
        if (carousel.dataset.initialised) return;
        carousel.dataset.initialised = 'true';

        const block = carousel.closest('.hero-carousel');
        const slides = carousel.querySelectorAll('.swiper-slide');

        // A single slide is already a finished hero — the markup renders it
        // correctly on its own, so there is nothing for Swiper to do.
        if (slides.length < 2) return;

        const reducedMotion = prefersReducedMotion();
        const autoplay = carousel.dataset.autoplay === 'true' && !reducedMotion;
        const delay = parseInt(carousel.dataset.autoplayDelay, 10) || 6000;

        new Swiper(carousel, {
            modules: [A11y, Autoplay, EffectFade, Pagination],
            effect: 'fade',
            // Without this the outgoing slide stays fully opaque until the new
            // one has finished arriving, which shows through as a flash of the
            // artwork underneath.
            fadeEffect: { crossFade: true },
            slidesPerView: 1,
            loop: true,
            speed: reducedMotion ? 0 : 800,
            autoplay: autoplay ? { delay, disableOnInteraction: false } : false,
            pagination: {
                el: block.querySelector('.hero-carousel__pagination'),
                clickable: true,
            },
            a11y: {
                enabled: true,
                containerRoleDescriptionMessage: 'carousel',
                itemRoleDescriptionMessage: 'slide',
                slideLabelMessage: 'Slide {{index}} of {{slidesLength}}',
                paginationBulletMessage: 'Go to slide {{index}}',
            },
            on: {
                afterInit: syncInertSlides,
                slideChangeTransitionStart: syncInertSlides,
            },
        });
    });
}

document.addEventListener('DOMContentLoaded', () => initHeroCarousel());

// Blocks are re-rendered as they are edited, so run again on each preview
// render (dispatched by resources/js/editor-block-init.js).
document.addEventListener('blockEditorInit', (e) => {
    if (e.detail && e.detail.block) {
        initHeroCarousel(e.detail.block);
    }
});
